import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ToolErrorStat } from "@/features/admin";
import { errorTone, pct, toneClass, when } from "../format";

/**
 * One row per tool with at least one error in the window, most failing first. Clicking a
 * row narrows the list below to that tool; clicking it again clears the filter.
 */
export function ToolTable({
  rows,
  isLoading,
  selected,
  onSelect,
}: {
  rows: ToolErrorStat[];
  isLoading?: boolean;
  selected?: string;
  onSelect: (tool: string | undefined) => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Par outil</CardTitle>
        <p className="text-xs text-muted-foreground">
          Le taux est calculé sur tous les appels de l'outil. Cliquez une ligne
          pour filtrer la liste des erreurs.
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucun outil en erreur sur la période.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Outil</TableHead>
                <TableHead className="text-right">Appels</TableHead>
                <TableHead className="text-right">Erreurs</TableHead>
                <TableHead className="text-right">Taux</TableHead>
                <TableHead className="text-right">Incidents</TableHead>
                <TableHead className="text-right">Rattrapées</TableHead>
                <TableHead className="text-right">Sessions</TableHead>
                <TableHead className="text-right">Clients</TableHead>
                <TableHead className="text-right">Dernière erreur</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.tool}
                  className={`cursor-pointer hover:bg-muted/50 ${
                    selected === row.tool ? "bg-muted/50" : ""
                  }`}
                  onClick={() =>
                    onSelect(selected === row.tool ? undefined : row.tool)
                  }
                >
                  <TableCell className="font-mono text-xs font-medium">
                    {row.tool || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.calls}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.errors}
                  </TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${toneClass(
                      errorTone(row.error_rate),
                    )}`}
                  >
                    {pct(row.error_rate)}
                  </TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${
                      row.incidents ? "text-red-600 dark:text-red-400" : ""
                    }`}
                  >
                    {row.incidents || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {row.recovered || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.sessions}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.clients}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground whitespace-nowrap">
                    {when(row.last_error_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
